import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useCustomToast } from 'design-system';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { GenerateMonthlyInvoicePayload, InvoiceGenerationResult } from './types';

const getEndpoint = (supplierId: number) => `api/v1/cms/reconciliation/suppliers/${supplierId}/invoices/monthly`;

type TData = InvoiceGenerationResult;
type TError = AxiosError;
type TVariables = GenerateMonthlyInvoicePayload;
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

/** Generates (or regenerates — same endpoint) the month-end consolidation invoice, synchronously.
 * BE errors surface verbatim per the spec: 409 "month is closed", 400 "No unpaid orders to bill
 * for this month", 404 no GPO profile. */
export function useGenerateMonthlyInvoiceApi(options?: Options): ReturnType {
  const toast = useCustomToast();

  const mutationFn = async ({ month, supplierId }: TVariables) => {
    try {
      const { data } = await http.post(getEndpoint(supplierId), undefined, { params: { month } });
      return data?.data ?? data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, { ...options, mutationKey: ['reconciliation-generate-monthly-invoice'] });
}
